import { TAGS } from "../constants";
import {
  PharmacyProvider,
  ProviderLicense,
  ProviderLicenseUpdate,
  ProviderProfile,
  ProviderSpecialty,
  ProviderSpecialtyUpdate,
  Specialties,
} from "../types";
import { api } from "./api";

export const providersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    createSpecialties: builder.mutation<
      ProviderSpecialty[],
      { provider_id: number; body: { specialties: Specialties[] } }
    >({
      query: ({ provider_id, body }) => ({
        url: `/api/providers/${provider_id}/specialties`,
        method: "POST",
        body,
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;

        dispatch(
          providersApi.util.updateQueryData(
            "getProviderSpecialties",
            args.provider_id,
            (draft) => {
              (draft || []).push(...data);
            },
          ),
        );
      },
    }),

    getProviderSpecialties: builder.query<ProviderSpecialty[], number>({
      query: (id) => ({
        url: `/api/providers/${id}/specialties`,
        method: "GET",
      }),
      providesTags: (_returns, _error, args) => [
        { type: TAGS.PROVIDER_SPECIALTIES, id: args },
      ],
    }),

    updateProviderSpecialty: builder.mutation<
      ProviderSpecialty,
      {
        provider_id: number;
        specialty: Specialties;
        updates: ProviderSpecialtyUpdate;
      }
    >({
      query: ({ provider_id, specialty, updates }) => ({
        url: `/api/providers/${provider_id}/specialties/${specialty}`,
        method: "PUT",
        body: updates,
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          providersApi.util.updateQueryData(
            "getProviderSpecialties",
            args.provider_id,
            (draft) => {
              draft.forEach((providerSpecialty, index, providerSpecialties) => {
                if (
                  providerSpecialty.provider_id === args.provider_id &&
                  providerSpecialty.specialty === args.specialty
                ) {
                  providerSpecialties[index] = {
                    ...providerSpecialty,
                    ...args.updates,
                  };
                }
              });
            },
          ),
        );

        queryFulfilled.catch(patch.undo);
      },
    }),

    createLicenses: builder.mutation<
      ProviderLicense[],
      { provider_id: number; body: { state_codes: string[] } }
    >({
      query: ({ provider_id, body }) => ({
        url: `/api/providers/${provider_id}/licenses`,
        method: "POST",
        body,
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;

        dispatch(
          providersApi.util.updateQueryData(
            "getProviderLicenses",
            args.provider_id,
            (draft) => {
              (draft || []).push(...data);
            },
          ),
        );
      },
    }),

    getProviderLicenses: builder.query<ProviderLicense[], number>({
      query: (id) => ({
        url: `/api/providers/${id}/licenses`,
        method: "GET",
      }),
      providesTags: (_returns, _error, args) => [
        { type: TAGS.PROVIDER_LICENSES, id: args },
      ],
    }),

    updateProviderLicense: builder.mutation<
      ProviderLicense,
      {
        provider_id: number;
        state_code: string;
        updates: ProviderLicenseUpdate;
      }
    >({
      query: ({ provider_id, state_code, updates }) => ({
        url: `/api/providers/${provider_id}/licenses/${state_code}`,
        method: "PUT",
        body: updates,
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        const patch = dispatch(
          providersApi.util.updateQueryData(
            "getProviderLicenses",
            args.provider_id,
            (draft) => {
              draft.forEach((license, index, licenses) => {
                if (
                  license.provider_id === args.provider_id &&
                  license.state_code === args.state_code
                ) {
                  licenses[index] = {
                    ...license,
                    ...args.updates,
                  };
                }
              });
            },
          ),
        );

        queryFulfilled.catch(patch.undo);
      },
    }),
    getProviderEPrescribeServices: builder.query<
      PharmacyProvider,
      { employeeId: string; serviceName: string }
    >({
      query: ({ employeeId, serviceName }) => ({
        method: "GET",
        url: `/api/providers/pharmacy-providers/${employeeId}`,
        params: {
          service_name: serviceName,
        },
      }),
      providesTags: (_returns, _error, args) => [
        { type: TAGS.PHARMACY_PROVIDERS, id: args.employeeId },
      ],
    }),
    getProviders: builder.query<ProviderProfile[], void>({
      query: () => ({
        url: `/api/providers`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetProvidersQuery,
  useCreateLicensesMutation,
  useGetProviderLicensesQuery,
  useUpdateProviderLicenseMutation,
  useCreateSpecialtiesMutation,
  useGetProviderSpecialtiesQuery,
  useUpdateProviderSpecialtyMutation,
  useGetProviderEPrescribeServicesQuery,
} = providersApi;
